import {
    useAppDisplayModeContext,
    useStyles,
    Container,
    Divider,
    useTheme,
} from '@archipro-design/aria';

import { useLocation } from '@remix-run/react';
import { NoSearchResults } from '@modules/professional/component/shared/NoSearchResults';

import * as S from '@modules/professional/component/shared/PaginatedStyles.style';

import { SearchFilter } from '@modules/professional/component/shared/SearchFilter';
import { useRef, useState } from 'react';
import FilterDialogMobile from '@modules/professional/component/shared/FilterDialog.mobile';
import type { DefaultElasticSearchResults } from '~/modules/professional/type';

import { useToggle } from 'ahooks';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { useSearchFilters } from '@modules/search/hook/use-search-filters';
import { useMatchesHandleData } from '~/modules/root/hook';
import type { ElasticSearchCategories } from '~/modules/search/type';

import { IN_PAGE_SEARCH_IGNORE_FILTER_KEYS } from '~/modules/professional/config/constants';
import { CategoryFilterNav } from '@modules/category/component/category-filter-section/CategoryFilterNav';
import AppliedFilters from '~/modules/search/components/search-filter-section/AppliedFilters';
import { resetFilterStates } from '~/modules/search/util/reset-filter-states';
import { Container as PageLayoutContainer } from '@modules/professional/component/page-layout/PageLayout.style';

import { Grid } from '@rocco/ui/layout/components/Grid';
import { Pagination as TilePagination } from '@rocco/ui/pagination/components/Pagination';
import { buildPaginationUrl } from '~/modules-rocco/pagination/util/build-pagination-url';
import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProjectTileViewModel as ProjectTileViewModelRocco } from '@rocco/ui/tile-project/view-models/project';
import { useCategoryTilesTracking } from '@ui-rocco/hooks/use-category-tiles-tracking';

interface PaginatedProjectsProp extends DefaultElasticSearchResults {
    projectTilesRocco: TileViewModel<ProjectTileViewModelRocco>[];
    disableTileLink?: boolean;
    disableSaveToDesignBoard?: boolean;
    disableSort?: boolean;
}

export const PaginatedProjects: React.FC<PaginatedProjectsProp> = ({
    pagination,
    projectTilesRocco,
    searchResult,
    filterStates,
    disableSaveToDesignBoard,
    disableSort,
}) => {
    const { css } = useStyles();
    const { siteVariables } = useTheme();
    const appDisplayMode = useAppDisplayModeContext();
    const { desktop } = appDisplayMode;

    const isBMPreview = useMatchesHandleData('isBMPreview', false);
    const remixLocation = useLocation();

    const [filterDrawerOpen, { toggle: toggleFilterDrawer }] =
        useToggle(!disableSort);

    const gridRef = useRef<HTMLDivElement | null>(null);
    useCategoryTilesTracking(gridRef);

    const tiles = projectTilesRocco.map((tile) => {
        return (
            <div
                key={tile.model.id}
                className="transform-gpu transition-transform duration-300"
            >
                <CategoryPageTileWithAdminActions
                    tile={{
                        ...tile,
                        section: 'ProfessionalProjects',
                        disableDesignBoard:
                            isBMPreview || disableSaveToDesignBoard,
                    }}
                />
            </div>
        );
    });

    const [openFilterDialog, setOpenFilterDialog] = useState(false);

    const totalCount = searchResult?.nbHits ?? 0;

    const navFilterButton = {
        children: `Filters`,
        transparent: true,
        onClick: () => setOpenFilterDialog(true),
    };

    const ignoreFilterKeys = IN_PAGE_SEARCH_IGNORE_FILTER_KEYS;
    const category = useMatchesHandleData<ElasticSearchCategories>(
        'category',
        'project'
    );

    const searchFilters = useSearchFilters({
        category,
        initialStates: filterStates,
        isCategoryPage: false,
        ignoreFilterKeys,
    });

    return (
        <Container>
            {desktop && (
                <div className={css(PageLayoutContainer)}>
                    <Divider
                        size={1}
                        variables={{
                            dividerPadding: 0,
                            dividerPrimaryColor:
                                siteVariables.colors.primitive.black,
                        }}
                    />
                </div>
            )}
            <div className={css(S.Wrap)}>
                <CollapsiblePageLayout
                    sidePanel={
                        desktop ? (
                            <div className={css(S.FilterSection)}>
                                <CategoryFilterNav
                                    category="project"
                                    searchFilters={searchFilters}
                                    hideCategorySectionTitle={true}
                                />
                            </div>
                        ) : null
                    }
                    hideSidePanel={!filterDrawerOpen}
                    content={
                        <>
                            <div className={css(S.FilterTogglesSection)}>
                                <SearchFilter
                                    searchFilters={searchFilters}
                                    filterButton={navFilterButton}
                                    initialStates={filterStates}
                                    filterDrawerOpen={filterDrawerOpen}
                                    toggleFilterDrawer={toggleFilterDrawer}
                                    disableSort={disableSort}
                                    disableToggleFilter={true}
                                />
                            </div>
                            {searchFilters.appliedFilters &&
                                searchFilters.appliedFilters.length > 0 && (
                                    <AppliedFilters
                                        filterItems={
                                            searchFilters.appliedFilters
                                        }
                                        resetFilters={() =>
                                            searchFilters.resetFilters(
                                                resetFilterStates(
                                                    searchFilters.filterStates,
                                                    [
                                                        ...ignoreFilterKeys,
                                                        'category',
                                                    ]
                                                )
                                            )
                                        }
                                    />
                                )}
                            <div className={css(S.ResultSection)}>
                                {!tiles.length ? (
                                    <NoSearchResults resultsType={'projects'} />
                                ) : (
                                    <>
                                        <Grid
                                            ref={gridRef}
                                            colClasses={
                                                'grid-cols-1 lg:grid-cols-3'
                                            }
                                            rowsGapClass="gap-x-2 gap-y-12 md:gap-x-6 md:gap-y-20"
                                            animate={false}
                                        >
                                            {tiles}
                                        </Grid>

                                        {pagination.totalPages > 1 && (
                                            <div className="pt-[6.5625rem] md:pt-20">
                                                <TilePagination
                                                    currentPath={remixLocation}
                                                    current={pagination.page}
                                                    total={
                                                        pagination.totalPages
                                                    }
                                                    urlBuilder={
                                                        buildPaginationUrl
                                                    }
                                                    visibleCount={
                                                        appDisplayMode.desktop
                                                            ? 4
                                                            : 3
                                                    }
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </>
                    }
                />

                <FilterDialogMobile
                    openFilterDialog={openFilterDialog}
                    itemCount={totalCount}
                    onClose={() => setOpenFilterDialog(false)}
                    filterComponent={<div>project-filters goes here</div>}
                />
            </div>
        </Container>
    );
};
